import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import { fetchShipmentAlerts, fetchShipmentList, getInStockLocations } from '../api';

import {
  AlertCardData,
  ShipmentFilters as FilterTypes,
  ShipmentStatus,
  ShipmentItem,
  ArrivalBucket,
  TripType,
  LoadType,
} from '../../../types/shipment';
import { getDivisions } from '../../settings/customers/api/customer';

import { DataTable, AlertCard, AlertFilter as DataTableAlertFilter, DivisionOption } from 'components/ui/data-table';
import { cpShipmentColumns } from './columns';
import { format, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from 'app/provider';

interface DateRange {
  from?: Date;
  to?: Date;
}

type CustomerOption = {
  cust_code: string;
  cust_name: string;
};

type PageAlertFilter = {
  arrival?: ArrivalBucket;
  trip_type?: TripType;
  load_type?: LoadType;
  pod?: boolean;
  freight_invoice?: boolean;
  shipment_status?: string;
};

type ShipmentsPageProps = {
  status: ShipmentStatus | 'all';
};

const STORAGE_KEY = "selectedCustId";

const mapToDataTableFilter = (filters: PageAlertFilter): DataTableAlertFilter => ({
  arrival: filters.arrival,
  trip_type: filters.trip_type,
  load_type: filters.load_type,
  pod: filters.pod,
  freight_invoice: filters.freight_invoice,
  shipment_status: filters.shipment_status,
});

const mapBucketIdToAlertFilter = (cardId: string, bucketId: string) => {
  switch (cardId) {
    case 'arrival':
      return { arrival: bucketId as ArrivalBucket };
    case 'trip_type':
      return { trip_type: bucketId as TripType };
    case 'load_type':
      return { load_type: bucketId as LoadType };
    default:
      return {};
  }
};

const mapAlertCardsToDataTable = (cards: AlertCardData[]): AlertCard[] =>
  cards.map(card => {
    let style: 'positive' | 'negative' | 'neutral' | 'total' = 'neutral';
    if (card.id === 'arrival') style = 'positive';
    else if (card.id === 'trip_type' || card.id === 'load_type') style = 'total';

    return {
      id: card.id,
      title: card.label,
      type: card.buckets?.length ? 'bucketed' : 'single',
      style,
      count: card.count,
      buckets: (card.buckets ?? []).map(bucket => ({
        id: bucket.id,
        label: bucket.label,
        count: bucket.count,
        filters: mapBucketIdToAlertFilter(card.id, bucket.id),
      })),
    };
  });

const buildKey = (obj: unknown) => JSON.stringify(obj);

const ShipmentsPage: React.FC<ShipmentsPageProps> = ({ status }) => {
  const { setBreadcrumbs, setHeaderBorder } = useBreadcrumbs();

  const didMountRef = useRef(false);
  const { userType, customers } = useAuth();
  const lastShipmentKeyRef = useRef<string | null>(null);
  const lastAlertKeyRef = useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({});

  const [shipmentStatus, setShipmentStatus] = useState<string | undefined>();

  const [shipments, setShipments] = useState<ShipmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [sorting, setSorting] = useState([{ id: 'cn_date', desc: true }]);

  const [alerts, setAlerts] = useState<AlertCardData[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);

  const [activeAlertFilters, setActiveAlertFilters] = useState<PageAlertFilter>({});

  const [selectedCustId, setSelectedCustId] = useState<string | undefined>();

  const [divisionOptions, setDivisionOptions] = useState<DivisionOption[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<number | undefined>();

  const [stockStatus, setStockStatus] = useState<string | undefined>();
  const [stockLocation, setStockLocation] = useState<string | undefined>();
  const [stockLocationOptions, setStockLocationOptions] = useState<
    { ou_code: string; branch_name: string }[]
  >([]);

  useEffect(() => {
    const titleCaseStatus = status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());

    setBreadcrumbs([
      { label: 'Shipments', href: '/shipments' },
      { label: titleCaseStatus },
    ]);

    setHeaderBorder(false);
    return () => setHeaderBorder(true);
  }, [status]);

  useEffect(() => {
    if (status !== "in_stock") return;

    const fetchLocations = async () => {
      try {
        const locations = await getInStockLocations();
        setStockLocationOptions(
          locations.map(loc => ({
            ou_code: loc.branch_ou_code,
            branch_name: loc.branch_name ?? loc.branch_ou_code,
          }))
        );
      } catch {
        setStockLocationOptions([]);
      }
    };
    fetchLocations();
  }, [status]);

  useEffect(() => {
    if (!customers || customers.length === 0) {
      setSelectedCustId(undefined);
      return;
    }

    const storedCustId = localStorage.getItem(STORAGE_KEY);

    const isStoredValid = storedCustId
      ? customers.some(c => c.cust_code === storedCustId)
      : false;

    if (isStoredValid) {
      setSelectedCustId(storedCustId!);
    } else {
      const defaultCust = customers[0].cust_code;
      setSelectedCustId(defaultCust);
      localStorage.setItem(STORAGE_KEY, defaultCust);
    }
  }, [customers]);

  useEffect(() => {
    if (selectedCustId) {
      localStorage.setItem(STORAGE_KEY, selectedCustId);
    }
  }, [selectedCustId]);

  useEffect(() => {
    if (!selectedCustId) {
      setDivisionOptions([]);
      setSelectedDivision(undefined);
      return;
    }

    getDivisions(selectedCustId)
      .then(res => {
        setDivisionOptions(res || []);
        setSelectedDivision(undefined);
      })
      .catch(() => {
        setDivisionOptions([]);
        setSelectedDivision(undefined);
      });
  }, [selectedCustId]);


  useEffect(() => {
    if (!didMountRef.current) return;

    setActiveAlertFilters({});
    setPagination(p => ({ ...p, pageIndex: 0 }));
  }, [status]);

  useEffect(() => {
    if (!selectedCustId) return;
    
    const key = buildKey({
      status,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      globalFilter,
      dateRange,
      sorting,
      activeAlertFilters,
      shipmentStatus,
      selectedDivision,
      selectedCustId,
      stockStatus,
      stockLocation,
    });

    if (lastShipmentKeyRef.current === key) return;
    lastShipmentKeyRef.current = key;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetchShipmentList({
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          status,
          search: globalFilter || undefined,
          from_date: dateRange.from ? format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss') : undefined,
          to_date: dateRange.to ? format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss') : undefined,
          division: selectedDivision || undefined,
          cust_id: selectedCustId,
          stock_status: stockStatus,
          stock_location: stockLocation,
          ...activeAlertFilters,
          sort_by: sorting[0]?.id || 'cn_date',
          sort_order: sorting[0]?.desc ? 'desc' : 'asc',
          shipment_status: shipmentStatus ?? activeAlertFilters.shipment_status,
        });

        setShipments(res.items || []);
        setTotalCount(res.total_items || 0);
        setPageCount(res.total_pages || 0);
      } catch {
        setShipments([]);
        setTotalCount(0);
        setPageCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [status, pagination.pageIndex, pagination.pageSize, globalFilter, dateRange, sorting, activeAlertFilters, shipmentStatus, selectedDivision, selectedCustId, stockStatus, stockLocation]);

  useEffect(() => {
    if (status !== 'in_transit') {
      setAlerts([]);
      return;
    }

    if (!selectedCustId) return;

    const key = buildKey({
      status,
      globalFilter,
      dateRange,
      activeAlertFilters,
      shipmentStatus,
      selectedDivision,
      selectedCustId,
    });

    if (lastAlertKeyRef.current === key) return;
    lastAlertKeyRef.current = key;

    setAlertsLoading(true);

    fetchShipmentAlerts({
      status,
      search: globalFilter || undefined,
      from_date: dateRange.from ? format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss') : undefined,
      to_date: dateRange.to ? format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss') : undefined,
      shipment_status: shipmentStatus ?? activeAlertFilters.shipment_status,
      division: selectedDivision || undefined,
      cust_id: selectedCustId,
      ...activeAlertFilters,
    })
      .then(res => setAlerts(res.cards || []))
      .catch(() => setAlerts([]))
      .finally(() => setAlertsLoading(false));
  }, [status, globalFilter, dateRange, activeAlertFilters, shipmentStatus, selectedDivision, selectedCustId]);

  useEffect(() => {
    didMountRef.current = true;
  }, []);

  const resetPage = () =>
    setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));

  const handleResetFilters = () => {
    invalidateKeys();
    setSelectedDivision(undefined);
    setShipmentStatus(undefined);
    setActiveAlertFilters({});
    setDateRange({});
    setGlobalFilter("");
    resetPage();
  };

  const handleAlertFilterChange = (filters: PageAlertFilter) => {
    setActiveAlertFilters(filters);
    resetPage();
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
    resetPage();
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range || {});
    resetPage();
  };

  const columnsWithRefresh = cpShipmentColumns(status, () => {
    lastShipmentKeyRef.current = null;
  });

  const handleShipmentStatusChange = (value: string | undefined) => {
    setShipmentStatus(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleDivisionChange = (divnId: number | undefined) => {
    setSelectedDivision(divnId);
    resetPage();
  };

  const handleCustomerChange = (custId: string | undefined) => {
    setSelectedCustId(custId);
    resetPage();
  };

  const invalidateKeys = () => {
    lastShipmentKeyRef.current = null;
    lastAlertKeyRef.current = null;
  };

  return (
    <div className="h-full w-full overflow-hidden flex flex-col">
      <div className="flex-1 min-h-0 overflow-hidden">
        <DataTable
          data={shipments}
          columns={columnsWithRefresh}
          loading={loading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={s => { setSorting(s); resetPage(); }}
          totalCount={totalCount}
          pageCount={pageCount}
          globalFilter={globalFilter}
          onGlobalFilterChange={handleGlobalFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          alertCards={mapAlertCardsToDataTable(alerts)}
          isLoadingAlerts={alertsLoading}
          shipmentStatus={shipmentStatus}
          onShipmentStatusChange={handleShipmentStatusChange}
          activeAlertFilters={mapToDataTableFilter(activeAlertFilters)}
          onAlertFilterChange={handleAlertFilterChange}
          currentTab={status}
          divisionOptions={divisionOptions}
          selectedDivision={selectedDivision}
          onDivisionChange={handleDivisionChange}
          customerOptions={customers}
          selectedCustId={selectedCustId}
          onCustomerChange={handleCustomerChange}
          userType={userType ?? undefined}
          onResetFilters={handleResetFilters}
          stockStatus={stockStatus}
          onStockStatusChange={(value) => {
            setStockStatus(value);
            resetPage();
          }}
          stockLocation={stockLocation}
          stockLocationOptions={stockLocationOptions}
          onStockLocationChange={(value) => {
            setStockLocation(value);
            resetPage();
          }}
        />

        {!loading && shipments.length === 0 && (
          <div className="py-4 text-center text-gray-500">No shipments found.</div>
        )}
      </div>
    </div>
  );
};

export default ShipmentsPage;
